import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'legacy-packages/ft-cb';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import ResultsBox from 'legacy-src/utility/ResultsBox';
import PracticeSkillBox from 'chameleon-packages/units/components/PracticeSkillBox';
import StudentAveragePopOver from './StudentAveragePopOver';

const { oneOfType, arrayOf, shape, string, any, array, bool } = PropTypes;

/**
 * Renders resources (topics and skills) for mobile
 */
const ResourcesMobile = (props) => {
  const { columns, items, isStudent } = props;
  let relevantItems = items.filter((item) => item.partsWithValues !== 0);
  const partsNameColumnMap = {};
  columns.forEach((column) => {
    if (column.isPartColumn) {
      partsNameColumnMap[column.label] = column;
    }
  });

  /**
   * Renders content by type
   * @param {string} contentType
   * @param {string} cellLabel The cell value
   * @param {object} otherProps
   * @return {any}
   * */
  const renderByType = (contentType, cellLabel, otherProps) => {
    if (cellLabel) {
      switch (contentType) {
        case 'text':
          return cellLabel;
        case 'cip':
          const label = cellLabel.split(':');
          return <PracticeSkillBox label={label[0].replace('Skill ', '')} />;
        case 'view-progress':
          return <div className="status-part in-progress">In Progress</div>;
        case 'awaiting-results':
        case 'refresh-for-results':
          return (
            <div className={`status-part ${contentType}`}>
              <div className="with-status-text">Awaiting Results</div>
            </div>
          );
        case 'take-quiz-begin':
          return (
            <div className="status-part take-quiz-begin">
              <div className="with-status">
                <Icon name="unlocked" />
              </div>
            </div>
          );
        case 'take-quiz-continue':
          return (
            <div className="status-part take-quiz-continue">
              <div className="with-status-text">In Progress</div>
            </div>
          );
        case 'locked':
        case 'unlock':
          return (
            <div className={`status-part is-locked`}>
              <Icon name="locked" />
            </div>
          );
        case 'score':
          const {
            cellClassName,
            users,
            questions,
            resultsPathname,
            isStudent,
          } = otherProps;
          return (
            questions && (
              <div className={`average-score ${cellClassName}`}>
                {(isStudent && (
                  <StudentAveragePopOver
                    label={cellLabel}
                    users={users}
                    questions={questions}
                    resultsPathname={resultsPathname}
                  />
                )) ||
                  cellLabel}
              </div>
            )
          );
        case 'no-score':
          return null;
        case 'not-assigned':
          return (
            <div className="status-part not-assigned">
              <div className="with-status-text">Not Assigned</div>
            </div>
          );
        case 'na':
          return (
            <div className="status-part na">
              <div className="with-status-text">na</div>
            </div>
          );
        default:
          throw Error(`Content type ${contentType} is not supported`);
      }
    } else {
      return null;
    }
  };

  const renderResultsBox = (partName, className, boxContent) => (
    <TableRow className={className} key={Math.random()}>
      <TableCell>{partName}</TableCell>
      <TableCell>{boxContent}</TableCell>
    </TableRow>
  );

  const renderHeaderAndFooter = (
    <ResultsBox className="results-box-unique">
      <Table>
        <TableBody>
          {Object.keys(partsNameColumnMap).map((partName) => {
            const { mobileActionColumnHeader, className, isCurrentPartColumn } =
              partsNameColumnMap[partName];
            if (isCurrentPartColumn) {
              return null;
            }
            return renderResultsBox(
              partName,
              className,
              mobileActionColumnHeader,
            );
          })}
        </TableBody>
      </Table>
    </ResultsBox>
  );

  const boxes = relevantItems.map((item) => {
    const { abbreviation } = item;

    return (
      <ResultsBox key={abbreviation}>
        <Table>
          <TableBody>
            {columns.map((column) => {
              let {
                mobile: { contentType },
                label,
                value: columnValue,
                className,
              } = column;
              if (!item[columnValue]) return;
              let {
                label: cellLabel,
                className: cellClassName,
                users,
                questions,
                resultsPathname,
                cellStatus,
              } = item[columnValue] || {};

              const otherProps = {
                cellClassName,
                users,
                questions,
                resultsPathname,
                isStudent,
              };

              //in this case we display 'na' on desktop
              if (!cellStatus && !cellLabel) {
                contentType = cellLabel = 'na';
                cellStatus = null;
              }

              return (
                <TableRow
                  key={`tr-${columnValue}-${abbreviation}`}
                  className={className}
                >
                  <TableCell>{label}</TableCell>
                  <TableCell>
                    {renderByType(
                      cellStatus || contentType,
                      cellLabel,
                      otherProps,
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </ResultsBox>
    );
  });

  return (
    <Fragment>
      {renderHeaderAndFooter}
      {boxes}
      {renderHeaderAndFooter}
    </Fragment>
  );
};

ResourcesMobile.displayName = 'ResourcesMobile';

ResourcesMobile.propTypes = {
  /**
   * Table th column info
   * */
  columns: oneOfType([
    arrayOf(
      shape({
        /**
         * Column label
         * */
        label: string,
        /**
         * Column className
         * */
        className: string,
        /**
         * Children
         * */
        children: any,
      }),
    ),
    array,
  ]),
  /**
   * Array of items for table body
   * */
  items: array,
  /**
   * User data bool
   * */
  isStudent: bool,
};

export default ResourcesMobile;
